import { useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import BikeImageComponentCanvas from './BikeImageComponentCanvas'
import BikeImageLogoCanvas from './BikeImageLogoCanvas'
import BikeImageWatermark from './BikeImageWatermark'
import { getImage } from '../utils/image-loader'
import { CANVAS_WIDTH, CANVAS_HEIGHT, toggleList } from '../utils/settings'
import getLightness from '../utils/getLightness'
import {
  componentClicked,
  componentToggle,
  componentHighlighted,
  componentUnhighlighted
} from '../actions'

const BikeImage = ({ isPdfBikeOnlyLayout }) => {
  const dispatch = useDispatch()
  const canvasCt = useRef(null)

  const isBikeOnlyLayout = useSelector((state) => state.isBikeOnlyLayout)
  const isPdfLayout = useSelector((state) => state.isPdfLayout)
  const bikeImage = useSelector((state) => state.bikeImage)
  const bikeConfig = useSelector((state) => state.bikeConfig)
  const background = useSelector((state) => state.bikeConfig.background)
  const angle = useSelector((state) => state.bikeConfig.angle)
  const { loaded, choices, highlightedComponentId, logos } = bikeImage
  const { color = '#eee' } = background
  const { baseModelId } = bikeConfig
  const canvasClass = [
    'velorbis-customizer-bike-canvas',
    !isBikeOnlyLayout ? 'velorbis-customizer-bike-canvas-fade' : '',
    !isBikeOnlyLayout && loaded
      ? 'velorbis-customizer-bike-canvas-faded-in'
      : '',
    highlightedComponentId
      ? 'velorbis-customizer-bike-canvas-has-highlight'
      : '',
    !isPdfLayout && baseModelId.includes('cargo') ? `bike-scaler ${angle}` : ''
  ].join(' ')

  const lightness = getLightness(color)

  const onClick = ({ clientX, clientY }) => {
    const componentId = componentIdAt(clientX, clientY)
    if (toggleList.find((item) => item === componentId)) {
      dispatch(componentToggle())
    } else if (componentId) {
      dispatch(componentClicked(componentId))
    }
  }

  const onMouseLeave = () => {
    if (highlightedComponentId != null) {
      dispatch(componentUnhighlighted())
    }
  }

  const onMouseMove = ({ clientX, clientY }) => {
    const componentId = componentIdAt(clientX, clientY)
    if (componentId) {
      if (componentId !== highlightedComponentId) {
        dispatch(componentHighlighted(componentId))
      }
    } else {
      if (highlightedComponentId != null) {
        dispatch(componentUnhighlighted())
      }
    }
  }

  const componentIdAt = (clientX, clientY) => {
    const canvases = canvasCt.current.getElementsByTagName('canvas')
    if (canvases.length === 0) {
      return null
    }
    const rect = canvases[0].getBoundingClientRect()
    let x = Math.round(((clientX - rect.left) * CANVAS_WIDTH) / rect.width)
    let y = Math.round(((clientY - rect.top) * CANVAS_HEIGHT) / rect.height)

    const speed = 10
    let dx = 0,
      dy = -speed
    let travelled = 0,
      dist = 1
    const max = Math.pow(5, 2)
    for (let t = 0; t < max; t++) {
      if (x >= 0 && x < CANVAS_WIDTH && y >= 0 && y < CANVAS_HEIGHT) {
        const p = (y * CANVAS_WIDTH + x) * 4 + 3
        for (let idx = choices.length - 1; idx >= 0; idx--) {
          const choice = choices[idx]
          if (choice.ignoreMouse) {
            continue
          }
          if (getImage(choice.imgUrl).getImageData().data[p] !== 0) {
            return choice.componentId
          }
        }
      }

      x += dx
      y += dy
      travelled++
      if (travelled === dist) {
        travelled = 0
        if (dx !== 0) {
          dist++
        }
        if (dx > 0) {
          dx = 0
          dy = speed
        } else if (dy > 0) {
          dx = -speed
          dy = 0
        } else if (dx < 0) {
          dx = 0
          dy = -speed
        } else {
          dx = speed
          dy = 0
        }
      }
    }
    return null
  }

  return (
    <div
      className={`velorbis-customizer-bike${
        isPdfBikeOnlyLayout ? ' velorbis-customizer-bike-for-pdf' : ''
      }`}
      onMouseLeave={onMouseLeave}
    >
      <div
        ref={canvasCt}
        className={canvasClass}
        onClick={onClick}
        onMouseMove={onMouseMove}
        style={
          isPdfBikeOnlyLayout ? { minWidth: '90vw', minHeight: '90vh' } : null
        }
      >
        {choices.map((item) => {
          const canvasProps = { ...item }
          if (baseModelId === 'william-kids-20') {
            if (item.componentId === 'base') {
              canvasProps.zIndex = 10
            }
            if (item.componentId === 'tires' || item.componentId === 'wheels') {
              canvasProps.zIndex = 9
            }
          }
          if (
            baseModelId === 'christian-kids-24' &&
            item.componentId === 'tires'
          ) {
            canvasProps.zIndex = 9
          }
          return (
            <BikeImageComponentCanvas
              key={item.componentId}
              highlighted={highlightedComponentId === item.componentId}
              {...canvasProps}
            />
          )
        })}
        {logos.map((logo, i) => (
          <BikeImageLogoCanvas
            key={`${logo.componentId}-logo-${angle}-${i}`}
            logo={logo}
          />
        ))}
        {isBikeOnlyLayout && (
          <BikeImageWatermark baseModelId={baseModelId} lightness={lightness} />
        )}
        {isPdfBikeOnlyLayout && (
          <BikeImageWatermark baseModelId={baseModelId} lightness={lightness} />
        )}
      </div>
    </div>
  )
}

export default BikeImage
